import React from 'react'
import { Link } from 'react-router-dom'
import {
  COLUMN_TYPES,
  COLUMN_SORT_SETTING,
  DATA_TYPES,
  SEARCH_TYPES,
  Icon,
  reducers,
  coreConstants,
} from '@adc/ui-morpheus'
import { EVENT_TEMPLATES } from '../../constants/tableTypes'
import { eventType as eventTypeList } from '../../constants/optionLists'

const { yesNo: yesNoList } = coreConstants.optionLists
const defaultColPropsWithWidth300 = {
  width: 300,
  isResizable: true,
  isReorderable: true,
  isVisible: false,
}
const defaultColPropsWithWidth100 = {
  width: 100,
  isResizable: true,
  isReorderable: true,
  isVisible: false,
}

export const tableConfig = {
  config: {
    entityKey: EVENT_TEMPLATES,
    primaryKeyField: 'event_template_id',
    enableSavedSearches: true,
  },
  fieldDefinitions: [
    {
      name: 'Selected',
      apiRef: 'selected', // not api val
      isUiOnly: true,
      columnDefinition: {
        type: COLUMN_TYPES.CHECKBOX,
        colProps: { width: 40, fixed: true },
      },
    },
    {
      name: 'Template Name',
      apiRef: 'event_template_name', // not api val
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        partialMatch: true,
        colProps: {
          ...defaultColPropsWithWidth300,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.SORTED,
          sortAscending: true,
          searchable: true,
        },
      },
    },
    {
      name: 'Type',
      apiRef: 'event_template_configuration.event_type',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: eventTypeList,
      formatOptions: {
        titleCase: true,
      },
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Trustee Sale',
      apiRef: 'event_template_configuration.trustee_sale',
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: yesNoList,
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Title',
      apiRef: 'event_title',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        partialMatch: true,
        colProps: {
          ...defaultColPropsWithWidth300,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Created Date',
      apiRef: 'created_date',
      dataType: DATA_TYPES.DATETIME_AS_LOCAL,
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: {
          ...defaultColPropsWithWidth300,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
        },
      },
    },
    {
      name: 'Created By',
      apiRef: 'created_by',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'Updated Date',
      apiRef: 'update_date',
      dataType: DATA_TYPES.DATETIME_AS_LOCAL,
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: {
          ...defaultColPropsWithWidth300,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
        },
      },
    },
    {
      name: 'Updated By',
      apiRef: 'updated_by',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'Status',
      apiRef: 'status',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: [
        { value: 'ACTIVE', text: 'Active' },
        { value: 'INACTIVE', text: 'Inactive' },
      ],
      formatOptions: {
        titleCase: true,
      },
      columnDefinition: {
        type: COLUMN_TYPES.STATUS,
        positiveValue: 'Active',
        negativeValue: 'Inactive',
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Edit',
      apiRef: '__actions__', // Not api val
      isUiOnly: true,
      columnDefinition: {
        type: COLUMN_TYPES.ACTION,
        actions: ['Link'],
        colProps: { width: 50, fixedRight: true, align: 'center' },
      },
    },
  ],
  filterClauses: [],
  sortBy: [
    {
      field: 'event_template_name.lowercase',
      order: 'asc',
    },
  ],
}

const linkColumnAction = () => (actionIndex, rowIndex, primaryId) =>
  (
    <Link
      style={{ margin: -8, padding: 8 }}
      key={actionIndex}
      to={`/schedule/event-templates/${primaryId}/edit`}
      data-elm-id={`action_edit_${rowIndex}`}
    >
      <Icon
        size='large'
        style={{ marginTop: -3 }}
        color='info'
        name='write square'
      />
    </Link>
  )

/**
 *
 * @param {*} fieldDefinitions
 * THIS SELECTOR IS TO MAINTAIN BACKWARDS COMPATIBILITY WITH EVERYTHING IN TOOLKIT
 * It will change/ be removed when everything there changes. This is so we can refactor slowly
 */
export const columnSelector = state =>
  state.fieldDefinitions
    .filter(field => field.columnDefinition)
    .map(col => {
      const column = {
        ...col.columnDefinition,
        ...{ field: col.apiRef, title: col.name },
      }
      if (column.actions) column.actions = column.actions.map(linkColumnAction)
      if (col.dataType) column.dataType = col.dataType
      if (col.searchType) column.searchType = col.searchType
      if (col.options) column.options = col.options
      if (col.formatOptions) column.formatOptions = col.formatOptions
      return column
    })

export const getFilterClauses = state => state.filterClauses

export default reducers.dataTable.dataTable(EVENT_TEMPLATES, tableConfig)
